import { flow, SnapshotIn, types, toGenerator, getSnapshot } from 'mobx-state-tree'
import { CenterSettingsModel } from '../models/CenterSettings/CenterSettingsModel'
import type { Center, CenterModel } from '../models/CenterSettings/CenterModel'
import type { DeploymentSettings } from '../models/CenterSettings/DeploymentSettingsModel'
import { OperationFailure, OperationInProgress, OperationNotStarted, OperationStatusModel, OperationSuccess } from '../models/common/OperationStatus'
import { FetchModel } from '../models/FetchModel'

export const CenterSettingsStore = FetchModel.named('CenterSettingsStore')
  .props({
    centerSettings: types.maybeNull(CenterSettingsModel),

    centerSettingsCopyForEditing: types.maybeNull(CenterSettingsModel),

    fetchStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    saveStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    sendTestEmailStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  })
  .views((self) => ({
    get getCentersUrl(): string {
      return `${self.baseUrl}centers/`
    },
    get deploymentSettingsUrl(): string {
      return `${self.baseUrl}deployment_settings/`
    },
    get getCenterTimezonesUrl(): string {
      return `${self.baseUrl}centers/center_timezones/`
    },
    saveCenterUrl(centerId: number): string {
      return `${self.baseUrl}centers/${centerId}/`
    },
    sendTestEmailUrl(centerId: number): string {
      return `${self.baseUrl}centers/${centerId}/send_test_email/`
    },
  }))
  .actions((self) => ({
    _fetchCenters: flow(function* _fetchCenters() {
      return yield* toGenerator(
        self.loadObjectOrError<Array<SnapshotIn<typeof CenterModel>>>(
          self.getCentersUrl,
          {},
          {fetchOperationName: 'fetch centers'} )
      )
    }),
    _fetchDeploymentSettings: flow(function* _fetchDeploymentSettings() {
      return yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<DeploymentSettings>>(
          self.deploymentSettingsUrl,
          {},
          {fetchOperationName: 'fetch deployment settings'} )
      )
    }),
    _fetchTimezones: flow(function* _fetchTimezones() {
      return yield* toGenerator(
        self.loadObjectOrError<Array<string>>(
          self.getCenterTimezonesUrl,
          { method: 'POST'}, // Why POST !?
          {fetchOperationName: 'fetch center timezones'} )
      )
    }),
  }))
  .actions((self) => ({
    fetchAndSetCenterSettings: flow(function* fetchCenterSettings() {
      self.fetchStatus = OperationInProgress()

      const fetchTimezonesResult = yield* toGenerator(self._fetchTimezones())
      if (fetchTimezonesResult.error) {
        console.error('fetchTimezonesResult', fetchTimezonesResult.error)
        self.fetchStatus = OperationFailure(fetchTimezonesResult.error)
        return
      }

      const fetchCentersResult = yield* toGenerator(self._fetchCenters())
      if (fetchCentersResult.error) {
        self.fetchStatus = OperationFailure(fetchCentersResult.error)
        return
      }

      const fetchDeploymentSettingsResult = yield* toGenerator(self._fetchDeploymentSettings())
      if (fetchDeploymentSettingsResult.error) {
        self.fetchStatus = OperationFailure(fetchDeploymentSettingsResult.error)
        return
      }

      const deliveryCenter = fetchCentersResult.result[0]
      if (deliveryCenter === undefined) {
        self.fetchStatus = OperationFailure({ name:'APIError', message: 'No centers found'})
        return
      }

      const centerSettingsSnapshot = {
        center: deliveryCenter,
        deploymentSettings: fetchDeploymentSettingsResult.result,
        timezones: fetchTimezonesResult.result
      }
      self.centerSettings = CenterSettingsModel.create(centerSettingsSnapshot)
      self.centerSettingsCopyForEditing = CenterSettingsModel.create(centerSettingsSnapshot)

      self.fetchStatus = OperationSuccess()
    }),
  }))
  .actions((self) => ({
    _saveCenter: flow(function* _saveCenter(center: Center) {
      return yield* toGenerator(
        self.loadObjectOrError<void>(
          self.saveCenterUrl(center.id), 
          { method: 'PATCH', body: JSON.stringify(getSnapshot(center)) }, 
          { fetchOperationName: 'Save center' }),
      )
    }),
    _saveDeploymentSettings: flow(function* _saveDeploymentSettings(deploymentSettings: DeploymentSettings) {
      return yield* toGenerator(
        self.loadObjectOrError<void>(
          self.deploymentSettingsUrl, 
          { method: 'POST', body: JSON.stringify(getSnapshot(deploymentSettings)) }, 
          { fetchOperationName: 'Save deployment settings' }),
      )
    })
  }))
  .actions((self) => ({
    saveCenterSettings: flow(function* fetchCenterSettings() {
      self.saveStatus = OperationInProgress()
      if (self.centerSettingsCopyForEditing === null) {
        throw Error('Cannot save deployment settings before loading')
      }
      // TODO: what if one succeeds and another fails?
      const saveCenterResult = yield* toGenerator(self._saveCenter(self.centerSettingsCopyForEditing.center))
      if (saveCenterResult.error) {
        self.saveStatus = OperationFailure(saveCenterResult.error)
        return
      }

      const saveDeploymentSettingsResult = yield* toGenerator(
        self._saveDeploymentSettings(self.centerSettingsCopyForEditing?.deploymentSettings)
      )
      if (saveDeploymentSettingsResult.error) {
        self.saveStatus = OperationFailure(saveDeploymentSettingsResult.error)
        return
      }
      
      self.saveStatus = OperationSuccess()
      void self.fetchAndSetCenterSettings()
    }),
    sendTestEmail: flow(function* sendTestEmail() {
      if (self.centerSettings === null) {
        throw Error('Cannot send test email before they were loaded')
      } 

      self.sendTestEmailStatus = OperationInProgress()

      const sendEmailLoadResult = yield* toGenerator(
        self.loadObjectOrError<void>(
          self.sendTestEmailUrl(self.centerSettings?.center.id), 
          { method: 'POST' }, 
          { fetchOperationName: 'Send test email' }),
      )

      if (sendEmailLoadResult.error) {
        self.sendTestEmailStatus = OperationFailure(sendEmailLoadResult.error)
        return
      }

      self.sendTestEmailStatus = OperationSuccess()
    }),
  }))